import {
  deleteBalltrackerByMachineUuidBlocked,
  deleteBalltrackerByMachineUuidStatTracking,
  deleteBalltrackerByMachineUuidUmpireAssist,
  getBalltrackerByMachineUuidUnifiedOptions,
  getMachinesSearchOptions,
  getMachinesSearchTaggingOptions,
  LEDModes,
  MachinePublicHydrated,
  postBalltrackerByMachineUuidBlocked,
  postBalltrackerByMachineUuidStatTracking,
  postBalltrackerByMachineUuidUmpireAssist,
} from "@aura-frontend/omnibus-az-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { isDefined } from "@/pages/Tagging/utils/isDefined";

type UpdateInput = {
  targetMode: LEDModes | null;
  // ISO-8601 end time string. Required when targetMode is non-null, ignored
  // when null. The caller picks this via the Enable dialog for additive
  // transitions, or pulls it from the machine's current lastActiveOccurrence
  // for partial-deactivate transitions (e.g. Stat+Umpire → Stat Only).
  endTimeISO: string | null;
  orgId: string | null;
  machine: Pick<
    MachinePublicHydrated,
    | "machineUuid"
    | "smartAvailableActive"
    | "umpireAssistActive"
    | "smartBlockedActive"
  >;
};

/**
 * Two-phase machine LED config update: clear every active entitlement, then
 * POST the entitlements that the new mode implies. Mirrors the old Automation
 * dialog's [useUpdateLedBoardConfig] flow. The umpire_assist mode posts BOTH
 * /stat_tracking and /umpire_assist (matching the old modal's behavior) so
 * the Stat-then-Umpire dependency holds regardless of whether the backend
 * implicitly activates stat when umpire is posted.
 */
export const useUpdateMachineLedMode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      targetMode,
      endTimeISO,
      orgId,
      machine,
    }: UpdateInput) => {
      const machineUuid = machine.machineUuid;

      const statIds = (machine.smartAvailableActive ?? [])
        .map((w) => w.id)
        .filter(isDefined);
      const umpireIds = (machine.umpireAssistActive ?? [])
        .map((w) => w.id)
        .filter(isDefined);
      const blockedIds = (machine.smartBlockedActive ?? [])
        .map((w) => w.id)
        .filter(isDefined);

      await Promise.all([
        ...statIds.map((id) =>
          deleteBalltrackerByMachineUuidStatTracking({
            path: { machine_uuid: machineUuid },
            query: { id },
            throwOnError: true,
          }),
        ),
        ...umpireIds.map((id) =>
          deleteBalltrackerByMachineUuidUmpireAssist({
            path: { machine_uuid: machineUuid },
            query: { id },
            throwOnError: true,
          }),
        ),
        ...blockedIds.map((id) =>
          deleteBalltrackerByMachineUuidBlocked({
            path: { machine_uuid: machineUuid },
            query: { id },
            throwOnError: true,
          }),
        ),
      ]);

      if (targetMode === null) return;
      if (!endTimeISO) {
        throw new Error("endTimeISO is required when enabling a LED mode");
      }

      const extra = orgId ? { orgId } : undefined;

      if (targetMode === "stat_tracking") {
        await postBalltrackerByMachineUuidStatTracking({
          path: { machine_uuid: machineUuid },
          body: { endTime: endTimeISO, ...(extra && { extra }) },
          throwOnError: true,
        });
      } else if (targetMode === "umpire_assist") {
        // Order matters: the backend rejects /umpire_assist with
        // "No current stat-tracking feature available to update into
        // umpire-assist" when no stat_tracking entitlement exists. Match the
        // old Automation modal's sequencing — stat_tracking lands first, then
        // umpire_assist gets layered on top.
        await postBalltrackerByMachineUuidStatTracking({
          path: { machine_uuid: machineUuid },
          body: { endTime: endTimeISO, ...(extra && { extra }) },
          throwOnError: true,
        });
        await postBalltrackerByMachineUuidUmpireAssist({
          path: { machine_uuid: machineUuid },
          body: { endTime: endTimeISO, ...(extra && { extra }) },
          throwOnError: true,
        });
      } else if (targetMode === "blocked") {
        await postBalltrackerByMachineUuidBlocked({
          path: { machine_uuid: machineUuid },
          body: { endTime: endTimeISO, ...(extra && { extra }) },
          throwOnError: true,
        });
      }
    },
    onSuccess: async (_data, vars) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getMachinesSearchOptions().queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: getMachinesSearchTaggingOptions().queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: getBalltrackerByMachineUuidUnifiedOptions({
            path: { machine_uuid: vars.machine.machineUuid },
          }).queryKey,
        }),
      ]);
    },
  });
};
